/**
 * <newsletter-popup>
 * Opens a native <dialog> once per visitor after a delay. Native showModal()
 * gives focus management, Escape to close and an inert background.
 *
 * Stored state (localStorage):
 *   { state: 'dismissed' | 'pending' | 'subscribed', at: timestamp }
 * - subscribed: never show again
 * - dismissed / pending: show again after `data-days` days
 */
const STORAGE_KEY = 'valam:newsletter-popup';
const DAY = 24 * 60 * 60 * 1000;
const RETRY_DELAY = 4000;
const MAX_RETRIES = 15;
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];

function readState() {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
  } catch (error) {
    return null;
  }
}

function writeState(state) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, at: Date.now() }));
  } catch (error) {
    // Storage blocked (private mode): the popup may show again, which is acceptable.
  }
}

class NewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.error = this.querySelector('[data-popup-error]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.formStep = this.querySelector('[data-popup-step="form"]');
    this.successStep = this.querySelector('[data-popup-step="success"]');
    this.designMode = this.dataset.designMode === 'true';
    this.subscribed = false;
    this.retries = 0;

    if (!this.dialog || !this.form) return;

    this.dialog.addEventListener('close', this.onClose);
    this.dialog.addEventListener('click', this.onDialogClick);
    this.form.addEventListener('submit', this.onSubmit);
    this.querySelectorAll('[data-popup-close]').forEach((button) => button.addEventListener('click', this.close));

    if (this.designMode) {
      document.addEventListener('shopify:section:select', this.onEditorSelect);
      document.addEventListener('shopify:section:load', this.onEditorSelect);
      document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      return;
    }

    if (this.dataset.enabled !== 'true') return;

    const saved = readState();
    const params = new URLSearchParams(window.location.search);

    // Back from Shopify's bot check after a fallback submit.
    if (params.get('customer_posted') === 'true' && saved?.state === 'pending') {
      writeState('subscribed');
      this.showSuccess();
      this.open();
      return;
    }

    if (!this.shouldShow(saved)) return;
    if (this.dataset.mobile !== 'true' && window.matchMedia('(max-width: 749px)').matches) return;

    // Start the delay only after the first interaction, so the popup image never
    // becomes the page's Largest Contentful Paint (LCP stops at first input).
    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
    this.startTimer = () => {
      this.removeInteractionListeners();
      this.timer = window.setTimeout(this.tryOpen, delay);
    };
    INTERACTION_EVENTS.forEach((type) =>
      window.addEventListener(type, this.startTimer, { once: true, passive: true })
    );
  }

  removeInteractionListeners() {
    if (!this.startTimer) return;
    INTERACTION_EVENTS.forEach((type) => window.removeEventListener(type, this.startTimer));
  }

  disconnectedCallback() {
    window.clearTimeout(this.timer);
    this.removeInteractionListeners();
    document.removeEventListener('shopify:section:select', this.onEditorSelect);
    document.removeEventListener('shopify:section:load', this.onEditorSelect);
    document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
  }

  shouldShow(saved) {
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    const days = Math.max(1, Number(this.dataset.days) || 14);
    return Date.now() - Number(saved.at || 0) > days * DAY;
  }

  /** Wait while another dialog (cart drawer, search, quick add) is open. */
  tryOpen = () => {
    const otherDialogOpen = Array.from(document.querySelectorAll('dialog[open]')).some((d) => d !== this.dialog);
    if (otherDialogOpen && this.retries < MAX_RETRIES) {
      this.retries += 1;
      this.timer = window.setTimeout(this.tryOpen, RETRY_DELAY);
      return;
    }
    if (!otherDialogOpen) this.open();
  };

  open() {
    if (this.dialog.open) return;
    this.dialog.showModal();
  }

  close = () => {
    if (this.dialog.open) this.dialog.close();
  };

  onClose = () => {
    if (this.designMode || this.subscribed) return;
    const saved = readState();
    if (saved?.state !== 'subscribed') writeState('dismissed');
  };

  /** A click on the dialog element itself is a click on the backdrop. */
  onDialogClick = (event) => {
    if (event.target === this.dialog) this.close();
  };

  onEditorSelect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.open();
  };

  onEditorDeselect = (event) => {
    if (event.detail?.sectionId === this.dataset.sectionId) this.close();
  };

  setError(message) {
    this.error.textContent = message || '';
    if (message) {
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    } else {
      this.input.removeAttribute('aria-invalid');
    }
  }

  setBusy(busy) {
    if (!this.submitButton) return;
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', String(busy));
  }

  showSuccess() {
    this.subscribed = true;
    this.formStep.hidden = true;
    this.successStep.hidden = false;
    const heading = this.successStep.querySelector('h2');
    if (heading?.id) this.dialog.setAttribute('aria-labelledby', heading.id);
    this.dialog.removeAttribute('aria-describedby');
    this.successStep.focus();
  }

  fallbackSubmit() {
    writeState('pending');
    HTMLFormElement.prototype.submit.call(this.form);
  }

  onSubmit = async (event) => {
    event.preventDefault();

    const email = this.input.value.trim();
    if (!email || !this.input.checkValidity()) {
      this.setError(this.dataset.errorMessage);
      return;
    }

    this.setError('');

    if (this.designMode) {
      this.showSuccess();
      return;
    }

    this.setBusy(true);

    try {
      const response = await fetch(this.form.action, {
        method: 'POST',
        body: new FormData(this.form),
        headers: { Accept: 'text/html' },
      });

      if (/\/challenge/.test(response.url)) {
        this.fallbackSubmit();
        return;
      }

      if (response.url.includes('customer_posted=true')) {
        writeState('subscribed');
        this.showSuccess();
        return;
      }

      const html = await response.text();
      const page = new DOMParser().parseFromString(html, 'text/html');
      const serverError = page.getElementById(this.error.id)?.textContent.trim();

      if (serverError) {
        this.setError(serverError);
      } else if (response.ok) {
        writeState('subscribed');
        this.showSuccess();
      } else {
        this.setError(this.dataset.errorMessage);
      }
    } catch (error) {
      this.fallbackSubmit();
    } finally {
      this.setBusy(false);
    }
  };
}

if (!customElements.get('newsletter-popup')) {
  customElements.define('newsletter-popup', NewsletterPopup);
}
